import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Eye } from "lucide-react";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { BaseItem, BaseItemDialog } from "./BaseItemDialog";

interface BaseItemSelectorProps<T extends BaseItem> {
  orgId: string;
  items: T[];
  isLoading: boolean;
  error: any;
  selectedItems: string[];
  setSelectedItems: (items: string[]) => void;
  customPrices: Record<string, number>;
  setCustomPrices: (prices: Record<string, number>) => void;
  customNames: Record<string, string>;
  setCustomNames: (names: Record<string, string>) => void;
  onUpdateDefaultItem?: (data: {
    itemId: string;
    cost?: number;
    title?: string;
  }) => void;
  isAddingItem?: boolean;
  setIsAddingItem?: (isAdding: boolean) => void;
  apiEndpoint: string;
  queryKey: string;
  itemType: string;
  itemTypeCapitalized: string;
  itemTypePlural: string;
  showDescription?: boolean;
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
}

export function BaseItemSelector<T extends BaseItem>({
  orgId,
  items,
  isLoading,
  error,
  selectedItems,
  setSelectedItems,
  customPrices,
  setCustomPrices,
  customNames,
  setCustomNames,
  onUpdateDefaultItem,
  isAddingItem = false,
  setIsAddingItem = () => {},
  apiEndpoint,
  queryKey,
  itemType,
  itemTypeCapitalized,
  itemTypePlural,
  showDescription = false,
  descriptionLabel,
  descriptionPlaceholder,
}: BaseItemSelectorProps<T>) {
  const { t } = useTranslation();
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [viewingItem, setViewingItem] = useState<T | null>(null);

  const handleItemClick = (item: T) => {
    const isSelected = selectedItems.includes(item.uid);

    if (isSelected) {
      // Remove item from selection
      setSelectedItems(selectedItems.filter((id) => id !== item.uid));
      // Clean up custom names and prices
      const newCustomNames = { ...customNames };
      const newCustomPrices = { ...customPrices };
      delete newCustomNames[item.uid];
      delete newCustomPrices[item.uid];
      setCustomNames(newCustomNames);
      setCustomPrices(newCustomPrices);
    } else {
      // Add item to selection
      setSelectedItems([...selectedItems, item.uid]);
      // Set default name and price from item data
      setCustomNames({
        ...customNames,
        [item.uid]: item.title,
      });
      setCustomPrices({
        ...customPrices,
        [item.uid]: item.cost,
      });
    }
  };

  const handleEditItem = (item: T) => {
    setEditingItem(item);
  };

  const handleViewItem = (item: T) => {
    setViewingItem(item);
  };

  const handleItemUpdated = (updatedItem: T) => {
    setEditingItem(null);
    if (onUpdateDefaultItem) {
      onUpdateDefaultItem({
        itemId: updatedItem.uid,
        cost: updatedItem.cost,
        title: updatedItem.title,
      });
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">{t("components.shared.baseItemSelector.loading")} {itemTypePlural}...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-4 text-red-500">
        {t("components.shared.baseItemSelector.errorLoading")} {itemTypePlural}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{itemTypePlural}</h3>
        <Button
          type="button"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsAddingItem(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("components.shared.baseItemSelector.addNew")} {itemTypeCapitalized}
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No {itemTypePlural.toLowerCase()} {t("components.shared.baseItemSelector.availableCreateYourFirst")}{" "}
          {itemType} {t("components.shared.baseItemSelector.toGetStarted")}
        </div>
      ) : (
        <div className="border rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => {
              const isSelected = selectedItems.includes(item.uid);
              const customPrice = customPrices[item.uid];
              const displayPrice =
                customPrice !== undefined ? customPrice : item.cost;

              return (
                <div
                  key={item.uid}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    isSelected
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => handleItemClick(item)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-sm truncate pr-2">
                      {item.title}
                    </h4>
                    <Badge variant="secondary" className="text-xs">
                      {formatCentsAsDollars(displayPrice)}
                    </Badge>
                  </div>

                  {showDescription && item.description && (
                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                      {item.description}
                    </p>
                  )}

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleViewItem(item);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEditItem(item);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Show total of selected items */}
      {selectedItems.length > 0 && (
        <div className="mt-4 p-3 bg-muted/30 rounded-md">
          <div className="flex justify-between">
            <span className="font-medium">
              {t("components.shared.baseItemSelector.selected")} {itemTypePlural}: {selectedItems.length}
            </span>
            <span className="font-semibold">
              $
              {formatCentsAsDollars(
                items
                  .filter((item) => selectedItems.includes(item.uid))
                  .reduce((sum, item) => {
                    const price =
                      customPrices[item.uid] !== undefined
                        ? customPrices[item.uid]
                        : item.cost;
                    return sum + price;
                  }, 0),
              )}
            </span>
          </div>
        </div>
      )}

      {/* Add Item Dialog */}
      <BaseItemDialog<T>
        isOpen={isAddingItem}
        onClose={() => setIsAddingItem(false)}
        mode="add"
        orgId={orgId}
        apiEndpoint={apiEndpoint}
        queryKey={queryKey}
        itemType={itemType}
        itemTypeCapitalized={itemTypeCapitalized}
        showDescription={showDescription}
        descriptionLabel={descriptionLabel}
        descriptionPlaceholder={descriptionPlaceholder}
        onItemAdded={(itemId, item) => {
          setIsAddingItem(false);
          // Auto-select the newly added item
          if (item) {
            setSelectedItems([...selectedItems, itemId]);
            setCustomNames({
              ...customNames,
              [itemId]: item.title,
            });
            setCustomPrices({
              ...customPrices,
              [itemId]: item.cost,
            });
          }
        }}
      />

      {/* Edit Item Dialog */}
      <BaseItemDialog<T>
        isOpen={!!editingItem}
        onClose={() => setEditingItem(null)}
        mode="edit"
        item={editingItem || undefined}
        orgId={orgId}
        apiEndpoint={apiEndpoint}
        queryKey={queryKey}
        itemType={itemType}
        itemTypeCapitalized={itemTypeCapitalized}
        showDescription={showDescription}
        descriptionLabel={descriptionLabel}
        descriptionPlaceholder={descriptionPlaceholder}
        onItemUpdated={handleItemUpdated}
      />

      {/* View Item Details Dialog */}
      <Dialog open={!!viewingItem} onOpenChange={() => setViewingItem(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{itemTypeCapitalized} {t("components.shared.baseItemSelector.details")}</DialogTitle>
            <DialogDescription>
              {t("components.shared.baseItemSelector.viewTheDetailsOf")} {itemType}.
            </DialogDescription>
          </DialogHeader>
          {viewingItem && (
            <div className="space-y-4">
              <div>
                <h4 className="font-medium">{t("components.shared.baseItemSelector.title")}</h4>
                <p className="text-sm text-gray-600">{viewingItem.title}</p>
              </div>
              <div>
                <h4 className="font-medium">{t("components.shared.baseItemSelector.cost")}</h4>
                <p className="text-sm text-gray-600">
                  {formatCentsAsDollars(viewingItem.cost)}
                </p>
              </div>
              {showDescription && viewingItem.description && (
                <div>
                  <h4 className="font-medium">{t("common.description")}</h4>
                  <p className="text-sm text-gray-600">
                    {viewingItem.description}
                  </p>
                </div>
              )}
              <div>
                <h4 className="font-medium">{t("components.shared.baseItemSelector.created")}</h4>
                <p className="text-sm text-gray-600">
                  {new Date(viewingItem.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setViewingItem(null)}>{t("common.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
